import { useRef, useCallback } from "react";
import { Sidebar } from "../components/SideBar";
import { DunningBanner } from "../components/DunningBanner";
import { WhatsAppTokenBanner } from "../components/WhatsAppTokenBanner";
import { Routes, Route, Navigate } from "react-router-dom";
import Home from "./Home";
import Settings from "./Settings";
import Account from "./Account";
import Reports from "./Reports";
import DailySummaries from "./DailySummaries";
import Cases from "./Cases";
import Documents from "./Documents";
import Knowledge from "./Knowledge";
import Login from "./Login";
import { useAuth } from "context/AuthContext";
import { usePageMeta } from "hooks/usePageMeta";
import { TermsModal } from "components/TermsModal";
import Spinner from "components/ui/Spinner";
import { BoardMembersProvider } from "context/BoardMembersContext";
import { ResidentsProvider } from "context/ResidentsContext";
import { WorkersProvider } from "context/WorkersContext";
import { ManagersProvider } from "context/ManagersContext";
import { ProvidersProvider } from "context/ProvidersContext";
import { PendingContactsProvider } from "context/PendingContactsContext";
import { TasksProvider } from "context/TasksContext";
import { CasesProvider } from "context/CasesContext";
import { CondosProvider } from "context/CondosContext";
import { OtherContactsProvider } from "context/OtherContactsContext";

export default function App() {
  const {
    isAuthenticated,
    managerId,
    subscriptionStatus,
    wappTokenReconnectRecommended,
    wappDisconnected,
    needsTermsAcceptance,
    currentTermsDocument,
    isImpersonating,
    refetchProfile,
    lang,
  } = useAuth();

  usePageMeta(lang);
  const isDunning = subscriptionStatus === "dunning";
  const bannerRows =
    (isDunning ? 1 : 0) +
    (wappTokenReconnectRecommended || wappDisconnected ? 1 : 0);

  const openHomeContactListRef = useRef<(() => void) | null>(null);
  const registerOpenHomeContactList = useCallback(
    (fn: (() => void) | null) => {
      openHomeContactListRef.current = fn;
    },
    [],
  );

  //const [isStudentListOpen, setIsStudentListOpen] = useState(false);
  // const [isFloatingChatOpen, setIsFloatingChatOpen] = useState(false);
  // const location = useLocation();

  // const closeFloatingChat = () => setIsFloatingChatOpen(false);

  // // Close FloatingChat when navigating to settings or when student list is opened
  // useEffect(() => {
  //   if (location.pathname === "/settings" || isStudentListOpen) {
  //     setIsFloatingChatOpen(false);
  //   }
  // }, [location.pathname, isStudentListOpen]);

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-[100dvh] w-screen max-w-[100vw] bg-background text-gray-900 items-center justify-center overflow-x-hidden px-3">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </div>
    );
  }

  if (!managerId) {
    return <Spinner className="h-screen" />;
  }

  if (needsTermsAcceptance && currentTermsDocument && !isImpersonating) {
    return (
      <TermsModal
        document={currentTermsDocument}
        onAccepted={refetchProfile}
      />
    );
  }

  return (
    <CondosProvider>
      <ResidentsProvider>
        <BoardMembersProvider>
          <WorkersProvider>
          <ManagersProvider>
            <ProvidersProvider>
              <PendingContactsProvider>
                <OtherContactsProvider>
                  <TasksProvider>
                    <CasesProvider>
                      <div
                        className={`flex h-[100dvh] min-h-0 w-screen max-w-[100vw] bg-gray-50 text-gray-900 overflow-hidden ${
                          bannerRows === 1
                            ? "pt-9"
                            : bannerRows === 2
                              ? "pt-[4.5rem]"
                              : ""
                        }`}
                      >
                        <DunningBanner />
                        <WhatsAppTokenBanner />
                        <Sidebar
                          bannerRows={bannerRows}
                          onOpenHomeContactList={() =>
                            openHomeContactListRef.current?.()
                          }
                        />
                        <div className="flex flex-1 min-w-0 min-h-0 overflow-hidden">
                          <Routes>
                            <Route
                              path="/"
                              element={
                                <Home
                                  registerOpenHomeContactList={
                                    registerOpenHomeContactList
                                  }
                                />
                              }
                            />
                            <Route path="/reports" element={<Reports />} />
                            <Route path="/daily-summaries" element={<DailySummaries />} />
                            <Route path="/cases" element={<Cases />} />
                            <Route path="/documents" element={<Documents />} />
                            <Route path="/knowledge" element={<Knowledge />} />
                            <Route path="/settings" element={<Settings />} />
                            <Route path="/account" element={<Account />} />
                            <Route path="*" element={<Navigate to="/" />} />
                          </Routes>
                        </div>
                        {/* <FloatingChat
                          managerId={managerId}
                          isOpen={isFloatingChatOpen}
                          onToggle={setIsFloatingChatOpen}
                        /> */}
                      </div>
                    </CasesProvider>
                  </TasksProvider>
                </OtherContactsProvider>
              </PendingContactsProvider>
            </ProvidersProvider>
          </ManagersProvider>
          </WorkersProvider>
        </BoardMembersProvider>
      </ResidentsProvider>
    </CondosProvider>
  );
}
